import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { ThinkingDialectSchema } from "../../../../models/thinking-dialect.registry";
import { ModelConfigBaseShape } from "../../../../schemas/ModelConfig/model-config.schema";
import { RetiredEffortLevelFieldsShape } from "../../../../schemas/ModelConfig/retired-effort-level-fields";
import { MAX_PROMPT_LENGTH } from "./agent-version.schemas";
import { AgentStatusSchema, CustomModelIdSchema, PaginationParamsSchema } from "./common.schemas";

// ============================================================================
// AGENT MODEL
// ============================================================================

/**
 * Legacy AgentModel enum — kept for backwards compatibility with the `model` field.
 *
 * 🚨 **No cast on the argument, deliberately.** `AGENT_MODEL_VALUES` is an
 * `as const` tuple, so `z.enum` infers the literal union and every consumer of
 * this schema inherits it. See `ModelProviderSchema` below for the full account
 * of what the widening spelling costs; both carried it, and both are gated by
 * `./v1-enum-inference.test.ts` now.
 */
export const AgentModelSchema = z.enum(DbEnum.AGENT_MODEL_VALUES);

/**
 * Model provider enum.
 *
 * 🚨 **No cast on the argument, deliberately.** `MODEL_PROVIDER_VALUES` is an
 * `as const` tuple, so `z.enum` infers the literal union — and every consumer of
 * this schema inherits it. Widening the argument to `[string, ...string[]]` (the
 * zod-3-era spelling this carried) left `z.infer` as bare `string`, which the
 * runtime parse still rejected correctly while the TYPE admitted anything: the
 * Public API v1 tracing filter reached a Prisma `where` clause through an
 * `as ModelProvider` cast for exactly that reason, and nothing but the one
 * `.parse()` in the controller stood between them.
 */
export const ModelProviderSchema = z.enum(DbEnum.MODEL_PROVIDER_VALUES);

/** The provider set the v1 contract accepts, inferred from the schema above. */
export type ModelProvider = z.infer<typeof ModelProviderSchema>;

/**
 * Model configuration — the modern way to set an agent's model.
 *
 * Use `GET /models` to list available models. The `modelId` from that response
 * goes into `modelConfig.modelName`, and the `provider` goes into `modelConfig.modelProvider`.
 *
 * 🚨 **Every tuning field is TAKEN from `ModelConfigBaseShape`, never restated.**
 * This object spent its life hand-spelling the same enums, and both halves of
 * that copy had already drifted from the canonical shape by the time anyone
 * looked:
 *
 * - the Anthropic level enum offered three levels where the canonical shape
 *   accepted eight, so `POST` and `PATCH /public/v1/agents` answered **400** on
 *   `"xhigh"`, and the same enum on the RESPONSE made a `GET` of those agents
 *   fail the API's own published contract.
 * - `thinkingDisplay` was absent entirely. This is a plain `z.object`, so zod
 *   STRIPS an undeclared key rather than refusing it: a caller sending it got a
 *   200 that persisted nothing, and — because `AgentRepository.update` replaces
 *   the stored `modelConfig` with the PARSED body — a read-modify-write through
 *   v1 silently ERASED the field on the 15 agents that carry it.
 *
 * `TaskModelTuningShape` (`./skills.schemas.ts`) is the same derivation on the
 * task surface, and its own comment named this file as the drifted one. This is
 * that comment being acted on rather than repeated.
 *
 * **Two fields are deliberately NOT taken from the base shape:**
 *
 * - `modelProvider` keeps `ModelProviderSchema`. The canonical shape spells it
 *   `z.nativeEnum(DbEnum.ModelProvider)`; this package publishes the provider
 *   set through `ModelProviderSchema` alone, and that pair is gated in two
 *   places (`../../../../models/provider-contract-surfaces.test.ts` and the
 *   SDK's `types-match-the-v1-contract.test.ts`). Swapping the construction
 *   here would move the published type for no drift — the VALUES are already
 *   identical.
 *
 * **A CUSTOM model is selected by `customModelId`, not by `modelProvider`.**
 * `GET /models` merges the organization's own rows in with `source: "custom"`
 * and `provider: "CUSTOM_<PROTOCOL>"`, which is NOT a member of `ModelProvider`
 * — so following the rule above on one of those rows is a 400 on the provider
 * enum. Put the row's `id` in `customModelId` instead and leave
 * `modelName` / `modelProvider` describing the platform model to fall back to.
 *
 * `customModelId` keeps `CustomModelIdSchema` for the same reason
 * `modelProvider` keeps `ModelProviderSchema`: the canonical shape deliberately
 * omits it (BYOM is scoped to the internal schema there) and the v1 WRITE bound
 * is uuid-checked, which is stricter than the canonical `z.string()`.
 */
export const ModelConfigSchema = z.object({
  modelName: ModelConfigBaseShape.modelName,
  modelProvider: ModelProviderSchema.describe(
    `Provider: ${DbEnum.MODEL_PROVIDER_VALUES.join(", ")}`
  ),
  reasoningLevel: ModelConfigBaseShape.reasoningLevel,
  thinkingDisplay: ModelConfigBaseShape.thinkingDisplay,
  temperature: ModelConfigBaseShape.temperature,
  customModelId: CustomModelIdSchema.optional()
});

/**
 * {@link ModelConfigSchema} as a request's `modelConfig` FIELD takes it — a nested field
 * schema, not a body, so it is lenient like the object it extends: the same fields, and the four
 * retired level names refused with a 400 naming `reasoningLevel` rather than stripped
 * with a 200 — see `RetiredEffortLevelFieldsShape`. The response keeps
 * {@link ModelConfigSchema}, which never carries them.
 */
export const ModelConfigInputSchema = ModelConfigSchema.extend(RetiredEffortLevelFieldsShape);

export const ModelSummarySchema = z.object({
  id: z.string().uuid(),
  modelId: z.string(),
  provider: z.string(),
  displayName: z.string(),
  modelName: z.string(),
  contextSize: z.number().nullable(),
  streaming: z.boolean(),
  /**
   * Thinking / reasoning dialect for this model.
   *
   * Absent ⇒ the model does not support any form of thinking. Replaces the legacy
   * boolean flags `supportsThinking` and `supportsReasoning`, which are still
   * emitted (derived) for backward compatibility.
   */
  thinkingDialect: ThinkingDialectSchema.nullable().optional(),
  /**
   * @deprecated Use `thinkingDialect` instead. Derived as
   * `thinkingDialect?.startsWith("anthropic-")`. Will be removed in the next major SDK release.
   */
  supportsThinking: z.boolean(),
  /**
   * @deprecated Use `thinkingDialect` instead. Derived as
   * `thinkingDialect === "openai-reasoning" || thinkingDialect === "kimi-effort"`.
   * Will be removed in the next major SDK release.
   *
   * `kimi-effort` is on the reasoning side of the split because its request shape is a
   * top-level effort level, like OpenAI's, rather than a thinking budget or display mode.
   * The SDK's own copy of this contract (`packages/sdk/src/types/models.ts`) states the
   * same derivation; `deriveLegacyFlags` in the v1 adapter is the implementation.
   */
  supportsReasoning: z.boolean(),
  deprecated: z.boolean(),
  /**
   * Whether the row is a platform model or one the organization defined.
   *
   * Emitted by both halves of the models repository — `listSystem` writes
   * `"system"`, `listCustom` writes `"custom"` — and this schema omitted it, so
   * the field shipped on every response while the contract said it did not
   * exist. The SDK had it and was right; the schema was the drifting side.
   */
  source: z.enum(["system", "custom"])
});

/**
 * A BARE ARRAY under `data`, not a `{ models: [...] }` wrapper.
 *
 * This schema declared the wrapper long after the handler stopped sending one.
 * `ListModelsUseCase` returns `[...system, ...custom]` and its own comment
 * records dropping the wrapper *for* the SDK contract, and `models.list()` is
 * typed `Promise<ModelSummary[]>` — so the wire and the published type agreed
 * with each other and this schema was the only dissenter (NEX-3868).
 *
 * Same direction as `source` above, one level up: the SDK was right and the
 * schema was the drifting side. "The contract is authoritative" is a good
 * default and it is not a law.
 *
 * Nothing went red because `v1-response-validation.interceptor` runs in `log`
 * mode by default, so a Response schema that disagrees with the wire writes a
 * line nobody reads. The same drift on the SDK side already cost one shipped
 * defect: a `Promise<{ models: X[] }>` return type over this flat array made
 * `nexus model list --json` print `{}` for 45 models — the shape of an empty
 * account. That half was fixed; the schema was never moved with it.
 *
 * Declared rather than omitted, deliberately. `ZAssets.AssetList` documents the
 * same bare-array shape by leaving `Response` off, but a descriptor with no
 * `Response` is outside every contract-derived check — 112 of them already are.
 * An array schema keeps the route validated AND states the shape.
 */
export const ListModelsResponseSchema = z.array(ModelSummarySchema);

// ============================================================================
// AGENT SUMMARY (for list endpoint)
// ============================================================================

export const AgentSummarySchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  shortBio: z.string().nullable(),
  profilePicture: z.string().nullable(),
  status: AgentStatusSchema,
  model: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable()
});

// ============================================================================
// AGENT DETAIL (for get endpoint - includes more fields)
// ============================================================================

export const AgentDetailSchema = AgentSummarySchema.extend({
  bio: z.string().nullable(),
  tags: z.string().nullable(),
  gender: z.string().nullable(),
  modelConfig: ModelConfigSchema.nullable(),
  modelName: z.string().nullable(),
  modelProvider: z.string().nullable(),
  playgroundFirstMessage: z.string().nullable(),
  prompt: z.string().nullable()
});

// ============================================================================
// LIST AGENTS
// ============================================================================

export const ListAgentsParamsSchema = PaginationParamsSchema.extend({
  status: AgentStatusSchema.optional(),
  search: z.string().optional()
});

export const ListAgentsResponseSchema = z.object({
  agents: z.array(AgentSummarySchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int()
  })
});

// ============================================================================
// GET AGENT
// ============================================================================

export const GetAgentResponseSchema = AgentDetailSchema;

// ============================================================================
// CREATE AGENT
// ============================================================================

/**
 * 🚨 **THE FLAT `modelName` / `modelProvider` PAIR IS A MIRROR OF THE SAME
 * STORED KEYS `modelConfig` WRITES, SO ITS VALIDATION MUST MATCH.**
 *
 * `AgentRepository` folds the flat pair into the JSON when no `modelConfig` is
 * supplied, and merges it over the stored config on update. Both land in
 * `Agent.modelConfig`, a Prisma `Json?` column that constrains nothing at rest.
 *
 * So while `modelProvider` here was `z.string().max(255)`, the enum on
 * `ModelConfigSchema` was defeated by the door beside it: measured against the
 * built contract, `modelConfig.modelProvider: "MISTRAL"` was REFUSED while a
 * flat `modelProvider: "MISTRAL"` on the same body was ACCEPTED, on both POST
 * and PATCH. One caller away from a stored value the READ contract cannot
 * describe — and the read side publishes `modelConfig` against the four-member
 * enum.
 *
 * `ModelProviderSchema` is what `ModelConfigSchema.modelProvider` already uses,
 * so flat and nested are now the same constraint by construction rather than by
 * two authors agreeing. `skills.schemas.ts` reached the same place independently
 * with `z.nativeEnum(DbEnum.ModelProvider)` — identical accepted set.
 *
 * ⚠️ This NARROWS a published request contract. A caller sending an
 * unrecognised provider now gets a 400 where it used to be accepted, which is
 * the correct outcome: the value was never storable in a shape the read
 * contract could describe. Production carries zero non-member rows across 1,340
 * agents (plus `AITask` and `AgentTask`), so nothing relies on it today.
 */
export const CreateAgentBodySchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  role: z.string().min(1).max(200),
  shortBio: z.string().max(500).optional(),
  bio: z.string().max(5000).optional(),
  tags: z.string().max(1000).optional(),
  gender: z.string().max(200).optional(),
  model: AgentModelSchema.optional(),
  modelConfig: ModelConfigInputSchema.optional(),
  modelName: z.string().max(255).optional(),
  modelProvider: ModelProviderSchema.optional(),
  playgroundFirstMessage: z.string().max(2000).nullable().optional(),
  /**
   * Initial system prompt (markdown). When provided, a CHECKPOINT version is
   * created and published for the new agent. Omit to start with an empty prompt.
   */
  prompt: z.string().max(MAX_PROMPT_LENGTH).optional()
});

export const CreateAgentResponseSchema = AgentDetailSchema;

// ============================================================================
// UPDATE AGENT
// ============================================================================

export const UpdateAgentBodySchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  role: z.string().min(1).max(200).optional(),
  shortBio: z.string().max(500).optional(),
  bio: z.string().max(5000).optional(),
  tags: z.string().max(1000).optional(),
  gender: z.string().max(200).optional(),
  model: AgentModelSchema.optional(),
  modelConfig: ModelConfigInputSchema.optional(),
  modelName: z.string().max(255).optional(),
  modelProvider: ModelProviderSchema.optional(),
  playgroundFirstMessage: z.string().max(2000).nullable().optional(),
  /**
   * Agent system prompt (markdown). There is no mutable prompt field on the
   * agent itself: setting this OVERWRITES the agent's draft, snapshots the
   * previous draft as an AUTO version, and creates a new CHECKPOINT version.
   * Whether that checkpoint also becomes what the agent SERVES is `autoPublish`.
   * Omit to leave the prompt unchanged.
   */
  prompt: z.string().max(MAX_PROMPT_LENGTH).optional(),
  /**
   * Publish the checkpoint that `prompt` creates, making it what every live
   * deployment serves. Defaults to `true`, so a prompt write reaches production
   * unless this says otherwise. Send `false` to edit the draft and keep the
   * currently published version live — the safe-experiment path.
   *
   * Ignored when `prompt` is absent: this endpoint publishes nothing on its own.
   *
   * On an agent that has NEVER published, `productionPromptVersionId` is null,
   * which already means "the draft is the live version" — so `false` there
   * changes the draft and the agent serves it regardless. The flag protects an
   * agent WITH a published version, which is the case that can hurt.
   */
  autoPublish: z.boolean().optional()
});

export const UpdateAgentResponseSchema = AgentDetailSchema;

// ============================================================================
// DELETE AGENT
// ============================================================================

export const DeleteAgentResponseSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true)
});

// ============================================================================
// DUPLICATE AGENT
// ============================================================================

export const DuplicateAgentResponseSchema = AgentDetailSchema;

// ============================================================================
// UPLOAD PROFILE PICTURE
// ============================================================================

export const UploadProfilePictureResponseSchema = z.object({
  profilePicture: z.string()
});
